<script lang="ts">
  import { onDestroy, onMount, type Snippet } from "svelte"
  import { mdiDotsHorizontal } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import CardWrapper from "@kontextwerk/web-sdk/ui/layout/CardWrapper.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import ProgressSteps from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import type { WidgetContextState } from "@kontextwerk/web-sdk/core"
  import { getWidgetContextSnapshot, updateWidgetContext } from "@kontextwerk/web-sdk/core"
  import InternalAssistMount from "../internal-assist-mount"
  import AiImportInstructionsOverlay from "./_modules/ai-import-instructions-overlay"
  import type { AiImportFlowAdapter, AiImportFlowConfig, AiImportFlowCustomStep } from "./contract"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    flow: AiImportFlowConfig
    widgetContextPatch?: Partial<WidgetContextState> | null
  }

  let {
    flow,
    widgetContextPatch = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let previousContext: WidgetContextState | null = null
  let hasTriggeredFreshSession = false
  let freshSessionRetryHandle: ReturnType<typeof setTimeout> | null = null

  const FRESH_SESSION_MAX_RETRIES = 300
  const FRESH_SESSION_RETRY_DELAY_MS = 100

  const chatEmbedHeightPx = $derived(flow.chatEmbedHeightPx ?? 920)
  const introTitle = $derived(flow.introTitle ?? "Was passiert?")
  const reviewTitle = $derived(flow.reviewTitle ?? "Ergebnisse prüfen")
  const reviewSnippet = $derived(flow.reviewSnippet ?? null)
  const reviewAction = $derived(flow.reviewAction ?? null)
  const showReviewStep = $derived(flow.showReviewStep ?? true)
  const adapterButtonPrefix = $derived(flow.adapterButtonPrefix ?? "Anleitung:")
  const hasAdapters = $derived((flow.adapters?.length ?? 0) > 0)
  const customSteps = $derived(flow.customSteps ?? [])
  const postChatSteps = $derived(flow.postChatSteps ?? [])
  const introSteps = $derived(
    flow.skipIntroStep
      ? []
      : [
          {
            id: "intro",
            label: "Erklärung",
            description: "",
            actionLabel: "Weiter",
            snippet: introStep,
          },
        ],
  )
  let reviewActionPending = $state(false)
  let widgetElement = $state<HTMLElement | null>(null)

  const toggleWidgetMenu = () => {
    const target = widgetElement as (HTMLElement & { toggleMenu?: () => void }) | null
    target?.toggleMenu?.()
  }

  const openInstructionOverlay = (adapter: AiImportFlowAdapter) => {
    openOverlay({
      title: adapter.label,
      content: AiImportInstructionsOverlay,
      closeOnParentOpen: false,
      properties: {
        instructions: adapter.instructions,
        subtitle: adapter.subtitle ?? null,
        introText: adapter.introText ?? null,
        outroText: adapter.outroText ?? null,
        note: adapter.note ?? null,
        hintText: flow.instructionsHintText ?? null,
      },
    })
  }

  const runReviewAction = async () => {
    if (!reviewAction || reviewActionPending) return

    reviewActionPending = true
    try {
      await reviewAction.onClick()
    } finally {
      reviewActionPending = false
    }
  }

  const clearFreshSessionRetry = () => {
    if (freshSessionRetryHandle !== null) {
      clearTimeout(freshSessionRetryHandle)
      freshSessionRetryHandle = null
    }
  }

  const tryStartFreshImportSession = (attempt = 0) => {
    if (hasTriggeredFreshSession) return

    if (typeof window === "undefined") return

    const global = window as typeof window & { chatWidgetContext?: Record<string, unknown> }
    const ctx = global.chatWidgetContext ?? {}
    const candidate =
      (ctx as { startNewChat?: unknown })?.startNewChat ??
      (window as { startNewChat?: unknown }).startNewChat

    if (typeof candidate === "function") {
      clearFreshSessionRetry()
      hasTriggeredFreshSession = true
      void Promise.resolve(candidate()).catch((error) => {
        console.warn("Assistent Widget: konnte keine frische Import-Session starten", error)
      })

      return
    }

    if (attempt >= FRESH_SESSION_MAX_RETRIES) return

    clearFreshSessionRetry()
    freshSessionRetryHandle = setTimeout(
      () => tryStartFreshImportSession(attempt + 1),
      FRESH_SESSION_RETRY_DELAY_MS,
    )
  }

  const triggerFreshSessionForChatStep = () => {
    if (hasTriggeredFreshSession) return

    tryStartFreshImportSession()
  }

  const triggerFreshSessionOnMount = (_node: HTMLElement) => {
    triggerFreshSessionForChatStep()

    return {
      destroy: () => {
        clearFreshSessionRetry()
      },
    }
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(null)

    previousContext = getWidgetContextSnapshot()
    const mergedPatch = {
      ...(widgetContextPatch ?? {}),
      ...(flow.chatStartProfileId ? { chatStartProfileId: flow.chatStartProfileId } : {}),
    }

    if (Object.keys(mergedPatch).length > 0) {
      updateWidgetContext(mergedPatch)
    }
  })

  onDestroy(() => {
    clearFreshSessionRetry()
    setActionButtonSnippet?.(null)

    if (previousContext) {
      updateWidgetContext(previousContext)
    }
  })

  const mapCustomStep = (step: AiImportFlowCustomStep) => ({
    id: step.id,
    label: step.label,
    description: step.description ?? "",
    actionLabel: step.actionLabel ?? "Weiter",
    hideDefaultAction: step.hideDefaultAction ?? false,
    disableNext: step.disableNext ?? false,
    onNext: step.onNext,
    snippet: step.snippet,
  })

  const steps = $derived.by<any[]>(() => {
    const configuredSteps = [
      ...introSteps,
      ...customSteps.map(mapCustomStep),
      {
        id: "chat",
        label: flow.chatStepLabel ?? "Chatbot",
        description: "",
        actionLabel: "Weiter",
        snippet: chatStep,
      },
      ...postChatSteps.map(mapCustomStep),
    ]

    if (!showReviewStep) return configuredSteps

    return [
      ...configuredSteps,
      {
        id: "review",
        label: "Prüfen",
        description: "",
        hideDefaultAction: true,
        snippet: reviewStep,
      },
    ]
  })
</script>

<ProgressSteps {steps} showControls={true} darkMode={true} syncUrl={false} />

{#snippet introStep()}
  <h3>{introTitle}</h3>
  <ul class="primary-list">
    {#each flow.introBullets as bullet, idx (idx)}
      <li>{bullet}</li>
    {/each}
  </ul>

  {#if flow.introHintText}
    <p class="hint">{flow.introHintText}</p>
  {/if}

  {#if hasAdapters}
    {#if flow.adapterSectionTitle}
      <h4 class="adapter-section-title">{flow.adapterSectionTitle}</h4>
    {/if}
    <div class="adapter-buttons">
      {#each flow.adapters ?? [] as adapter (adapter.id)}
        <CTA
          type="tertiaryButton"
          darkMode={true}
          content={adapter.buttonLabel ?? `${adapterButtonPrefix} ${adapter.label}`}
          onClick={() => openInstructionOverlay(adapter)}
        />
      {/each}
    </div>
  {/if}
{/snippet}

{#snippet chatStep()}
  <div class="chat-step-enter" use:triggerFreshSessionOnMount>
    <CardWrapper white={true}>
      {#snippet rightCorner()}
        <button
          class="btn widget-menu-button"
          type="button"
          onclick={toggleWidgetMenu}
          title="Weitere Optionen"
          aria-label="Weitere Optionen"
        >
          <Icon path={mdiDotsHorizontal} />
        </button>
      {/snippet}
      {#snippet header()}
        <SectionTitle
          title={flow.chatStepTitle ?? "Assist"}
          small={true}
          helpModalTextId={flow.chatStepHelpModalTextId ?? ""}
          helpModalTooltip={flow.chatStepHelpModalTooltip ?? ""}
        />
      {/snippet}
      <InternalAssistMount
        embed
        style={`display: flex; min-height: ${chatEmbedHeightPx}px; height: ${chatEmbedHeightPx}px; width: 100%;`}
        bind:widgetElement
      />
    </CardWrapper>
  </div>
{/snippet}

{#snippet reviewStep()}
  <h3>{reviewTitle}</h3>
  {#if reviewSnippet}
    {@render reviewSnippet()}
  {:else}
    <ul class="primary-list">
      {#each flow.reviewBullets as bullet, idx (idx)}
        <li>{bullet}</li>
      {/each}
    </ul>
  {/if}
  {#if reviewAction}
    <div class="review-actions">
      <CTA
        type="primaryButton"
        darkMode={true}
        content={reviewAction.label}
        icon={reviewAction.icon ?? ""}
        disabled={reviewActionPending}
        onClick={runReviewAction}
      />
    </div>
  {/if}
{/snippet}

<style lang="less">
  h3 {
    margin-bottom: 1.2rem;
  }

  .adapter-section-title {
    margin-top: 1.2rem;
    margin-bottom: 0.6rem;
  }
  .widget-menu-button {
    color: white !important;
  }
  .adapter-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-top: 0.4rem;
  }
  .hint {
    color: var(--text-100);
  }
  .review-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.2rem;
  }
</style>
